import { useEffect, useState } from "react";
import SectionHead from "./SectionHead";
import useLang from "../i18n/LanguageContext";
import useInView from "../hooks/useInView";
import useMediaQuery from "../hooks/useMediaQuery";
import "./Experience.css";

const AUTOPLAY_MS = 8000;
const TYPE_MS = 38;
const num = (i) => String(i + 1).padStart(2, "0");

// Each job is a key; pressing one types that job onto the screen next to it.
const Experience = () => {
  const { t } = useLang();
  const e = t.experience;
  const jobs = e.items;
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [ref, inView] = useInView(0.3);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [typed, setTyped] = useState(0);
  const role = jobs[index].role;

  // Type the role out letter by letter each time a job is picked
  useEffect(() => {
    if (!inView) return undefined;
    if (reducedMotion) {
      setTyped(role.length);
      return undefined;
    }
    setTyped(0);
    const id = setInterval(() => {
      setTyped((n) => {
        if (n >= role.length) {
          clearInterval(id);
          return n;
        }
        return n + 1;
      });
    }, TYPE_MS);
    return () => clearInterval(id);
  }, [inView, reducedMotion, role]);

  // Move on to the next job unless the visitor is reading this one
  const autoplay = inView && !paused && !reducedMotion;
  useEffect(() => {
    if (!autoplay) return undefined;
    const id = setTimeout(() => setIndex((i) => (i + 1) % jobs.length), AUTOPLAY_MS);
    return () => clearTimeout(id);
  }, [autoplay, index, jobs.length]);

  return (
    <section id="experience" className="section">
      <div className="section__inner">
        <SectionHead fkey="F3" title={e.title} intro={e.intro} />

        <div
          ref={ref}
          className={`xp${inView ? " is-in" : ""}`}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={(ev) => {
            if (!ev.currentTarget.contains(ev.relatedTarget)) setPaused(false);
          }}
        >
          <div className="xp__keys">
            {jobs.map((job, i) => (
              <button
                key={job.role + job.company}
                type="button"
                className={`xp__key${i === index ? " is-active" : ""}`}
                aria-pressed={i === index}
                data-critter={`xp-${i}`}
                onClick={() => setIndex(i)}
              >
                <span className="xp__key-num">{num(i)}</span>
                <span className="xp__key-name">{job.company}</span>
                <span className="xp__key-period">{job.period}</span>
                {i === index && autoplay && (
                  <span
                    className="xp__progress"
                    style={{ animationDuration: `${AUTOPLAY_MS}ms` }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* All jobs share one grid cell, so the screen is as tall as the longest */}
          <div className="xp__screen" aria-live="polite">
            {jobs.map((job, i) => {
              const active = i === index;
              const shown = active ? typed : job.role.length;
              return (
                <article
                  key={job.role + job.company}
                  className={`xp__job${active ? " is-active" : ""}`}
                  aria-hidden={!active}
                >
                  <span className="xp__meta">
                    {num(i)} / {num(jobs.length - 1)} · {job.period}
                  </span>
                  <h3 className="xp__role" aria-label={job.role}>
                    <span aria-hidden="true">{job.role.slice(0, shown)}</span>
                    <span className="xp__caret" aria-hidden="true" />
                    <span className="xp__rest" aria-hidden="true">{job.role.slice(shown)}</span>
                  </h3>
                  <span className="xp__company">{job.company}</span>
                  <ul className="xp__points">
                    {job.points.map((point, k) => (
                      <li key={point} style={{ animationDelay: `${0.5 + k * 0.14}s` }}>
                        {point}
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
